<script lang="ts" setup>
import { ComponentPublicInstance, computed, ref, watch } from 'vue'

import HistoryItemTableRow from '@mobile/views/Relation/DetailsPanel/Table/HistoryItemTableRow.vue'
import { useDockedIncidentComponent } from '@mobile/views/Relation/DetailsPanel/Table/useDockedIncidentComponent'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { Result } from '../../Shared/IncidentResult'
import { useHistoryIncidents } from './useHistoryIncidents'

type Props = {
  linkedIncident: ProbableLinkedIncident
  result: Result | null
  dockable?: boolean
  pinned?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  dockable: true,
})

const eventInjections = useEventInjections()
const { isIncidentSelected } = useHistoryIncidents(eventInjections)
const {
  handleRowClick,
  setInitialDockPositionFromRow,
  shouldStopRowClickPropagation,
} = useDockedIncidentComponent(eventInjections)
const placeholderRow = ref<ComponentPublicInstance | null>(null)
const isSelectedRow = computed(
  () => props.dockable && isIncidentSelected(props.linkedIncident),
)

watch(
  [isSelectedRow, placeholderRow, () => props.linkedIncident.incident.id],
  () => {
    if (!isSelectedRow.value) {
      return
    }

    setInitialDockPositionFromRow(props.linkedIncident, placeholderRow.value)
  },
  { flush: 'post' },
)

function handleClick(event: PointerEvent): void {
  handleRowClick(props.linkedIncident, event, props.dockable)

  if (shouldStopRowClickPropagation(event)) {
    event.stopPropagation()
  }
}
</script>

<template>
  <HistoryItemTableRow
    ref="placeholderRow"
    v-bind="{ linkedIncident, result, pinned }"
    :selectIncidentOnModalOpen="!pinned"
    :aria-hidden="isSelectedRow"
    @click.capture="handleClick"
  />
</template>
